import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { capitalised, countOf, type BlockNoun } from "../lib/blockNoun";
import {
  blockNounForVersion,
  getEngagement,
  getRun,
  latestEvaluation,
  listActionLinks,
  listActions,
  listEntities,
  listFindings,
  listOrganisations,
  listOriginsForFindings,
  listPublishedVersions,
  memberDirectory,
  runInput,
} from "../lib/api";
import { inputFingerprint } from "../lib/fingerprint";
import {
  buildReportModel,
  type Currency,
  type ReportModel,
} from "../lib/reportTemplate";
import type { EvaluationResult, Finding } from "../lib/types";
import { useBreadcrumb } from "../shell/ContextHeader";

// The assessment working report (F4.5): ONE template, rendered from the
// FROZEN evaluation snapshot and the managed register. The console computes
// nothing here — "the kernel calculates; the console renders" applies to
// paper exactly as it applies to screens, and every number below is read
// verbatim from the persisted result (gates the results screen already
// carries: dual score ADR-23, coverage/evidence separation ADR-16, verdict
// qualification ADR-30, denominators ADR-25).
//
// Printing IS the Release 1 output path: the print stylesheet strips the
// shell and the browser's print-to-PDF produces the document. No server
// renders reports; nothing leaves the tenant boundary.
//
// A STALE or currency-unknown report renders — a consultant may need the
// historical document — but carries the banner in print as on screen; the
// fingerprint contract (BACKLOG §11) says stale must never present as
// current, and that includes paper.

const num = (n: number | null): string =>
  n === null ? "—" : Number.isInteger(n) ? String(n) : n.toFixed(2);

const SEV_ORDER: Record<Finding["severity"], number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "empty"; message: string }
  | { kind: "ready"; model: ReportModel; noun: BlockNoun };

export function RunReport() {
  const { orgId, runId } = useParams<{ orgId: string; runId: string }>();
  const { setTrail } = useBreadcrumb();
  const [state, setState] = useState<State>({ kind: "loading" });

  const load = useCallback(async () => {
    if (!orgId || !runId) return;
    try {
      const [orgs, run, evaluation] = await Promise.all([
        listOrganisations(),
        getRun(runId),
        latestEvaluation(runId),
      ]);
      const org = orgs.find((o) => o.id === orgId);
      if (!org || !run || run.organisation_id !== orgId) {
        setState({ kind: "error", message: "run not found, or you are not a member" });
        return;
      }
      if (!evaluation) {
        setState({
          kind: "empty",
          message:
            "This run has no evaluation yet, so there is nothing to report. A report renders the frozen result; it never manufactures one.",
        });
        return;
      }
      const [engagement, entities, versions, findings, actions, links, members, noun] =
        await Promise.all([
          getEngagement(run.engagement_id),
          listEntities(orgId),
          listPublishedVersions(),
          listFindings(runId),
          listActions(orgId),
          listActionLinks(orgId),
          memberDirectory(orgId),
          blockNounForVersion(run.instrument_version_id),
        ]);
      const origins = await listOriginsForFindings(findings.map((f) => f.id));

      let currency: Currency;
      try {
        const fp = await inputFingerprint(await runInput(runId));
        currency = fp === evaluation.input_hash ? { kind: "current" } : { kind: "stale" };
      } catch (e) {
        currency = { kind: "unknown", reason: (e as Error).message };
      }

      const version = versions.find((v) => v.id === run.instrument_version_id);
      const model = buildReportModel({
        identity: {
          clientName: org.name,
          engagementName: engagement?.name ?? "—",
          entityName: entities.find((e) => e.id === run.entity_id)?.name ?? "—",
          runTitle: run.title ?? "Untitled run",
          runState: run.state,
          instrumentTitle: version?.instruments?.title ?? "(version no longer offered)",
          instrumentVersion: version?.version ?? "—",
        },
        evaluation,
        currency,
        findings,
        origins,
        actions,
        links,
        members,
        now: new Date(),
      });
      setState({ kind: "ready", model, noun });
      setTrail([
        { label: "Clients & engagements", to: "/clients" },
        { label: org.name, to: `/clients/${org.id}` },
        { label: run.title ?? "Untitled run", to: `/clients/${org.id}/runs/${run.id}` },
        { label: "Report" },
      ]);
    } catch (e) {
      setState({ kind: "error", message: (e as Error).message });
    }
  }, [orgId, runId, setTrail]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === "loading") return <p>Loading…</p>;
  if (state.kind === "error")
    return <div className="error-banner">Could not load: {state.message}</div>;
  if (state.kind === "empty")
    return (
      <div className="card empty-state">
        <h3>No evaluation to report</h3>
        <p>{state.message}</p>
      </div>
    );

  const { model, noun } = state;
  const r: EvaluationResult = model.result;

  return (
    <div className="report">
      <div className="report-toolbar no-print" style={{ display: "flex", gap: "0.6rem", alignItems: "center", marginBottom: "1rem" }}>
        <button className="btn" onClick={() => window.print()}>
          Print / save as PDF
        </button>
        <span style={{ color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>
          The browser's print dialog produces the document; nothing is uploaded
          anywhere.
        </span>
      </div>

      {/* The audience label comes from the TEMPLATE, deliberately. */}
      <div className="report-watermark">{model.template.label}</div>

      <header style={{ marginBottom: "1rem" }}>
        <h1 style={{ marginBottom: "0.2rem" }}>Assessment working report</h1>
        <p style={{ margin: 0, color: "var(--text-muted)" }}>
          {model.identity.instrumentTitle} — v{model.identity.instrumentVersion}
        </p>
      </header>

      <section className="card">
        <dl className="report-grid">
          <dt>Client</dt>
          <dd>{model.identity.clientName}</dd>
          <dt>Engagement</dt>
          <dd>{model.identity.engagementName}</dd>
          <dt>Assessed entity</dt>
          <dd>{model.identity.entityName}</dd>
          <dt>Run</dt>
          <dd>
            {model.identity.runTitle} ({model.identity.runState.replaceAll("_", " ")})
          </dd>
          <dt>Evaluated</dt>
          <dd>{new Date(model.evaluation.evaluatedAt).toLocaleString()}</dd>
          <dt>Report generated</dt>
          <dd>{new Date(model.generatedAt).toLocaleString()}</dd>
        </dl>
      </section>

      {model.currency.kind !== "current" && (
        <div className="report-stale">
          {model.currency.kind === "stale" ? (
            <>
              <strong>Stale.</strong> Responses have changed since this
              evaluation; this report reflects them as they were then.
            </>
          ) : (
            <>
              <strong>Currency unknown.</strong> Whether responses have changed
              could not be determined ({model.currency.reason}). Do not treat
              this report as current.
            </>
          )}
        </div>
      )}

      {!r.validation.ok && (
        <section className="card" style={{ borderLeft: "3px solid var(--state-danger)" }}>
          <h2>Input validation failed</h2>
          <p style={{ color: "var(--text-muted)" }}>
            Problems with the assessment input, not the assessed entity. No
            verdict is issued while they stand (ADR-30).
          </p>
          <ul>
            {r.validation.issues.map((i, idx) => (
              <li key={idx}>
                <strong>{i.severity}</strong> {i.code}: {i.message}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="card">
        <h2>Formal verdict</h2>
        {r.formal_verdict === null ? (
          <p style={{ margin: 0 }}>
            <strong>Withheld.</strong>{" "}
            {r.formal_verdict_withheld ?? "The engine did not state why."}
          </p>
        ) : (
          <p style={{ margin: 0, fontSize: "1.25rem" }}>
            Maturity level <strong>{num(r.formal_verdict.mean_maturity_level)}</strong>{" "}
            across {r.formal_verdict.block_count} contributing{" "}
            {r.formal_verdict.block_count === 1 ? noun.one : noun.many} —{" "}
            {r.formal_verdict.qualification.status}
            {r.formal_verdict.qualification.reasons.length > 0 &&
              ` (${r.formal_verdict.qualification.reasons.join("; ")})`}
            {r.formal_verdict.capped && (
              <span style={{ color: "var(--state-danger)" }}>
                {" "}
                — capped{r.formal_verdict.cap_label ? `: ${r.formal_verdict.cap_label}` : ""}
              </span>
            )}
          </p>
        )}
      </section>

      <section className="card">
        <h2>Overall</h2>
        <dl className="report-grid">
          <dt>Mean maturity level</dt>
          <dd>
            {num(r.overall.mean_maturity_level)} across {countOf(r.overall.block_count, noun)}
          </dd>
          <dt>Mean raw score (ADR-23)</dt>
          <dd>{num(r.overall.mean_raw_score)}</dd>
          <dt>Coverage</dt>
          <dd>
            {r.overall.coverage.blocks_covered}/{r.overall.coverage.blocks_applicable}{" "}
            {noun.many}, {r.overall.coverage.criteria_covered}/
            {r.overall.coverage.criteria_applicable} criteria
          </dd>
          <dt>Evidence (separate, ADR-16)</dt>
          <dd>
            {r.overall.evidence.linked}/{r.overall.evidence.required} required links —{" "}
            {r.overall.evidence.sufficient ? "sufficient" : "insufficient"}
          </dd>
          <dt>Qualification</dt>
          <dd>
            {r.overall.qualification.status}
            {r.overall.qualification.reasons.length > 0 &&
              ` (${r.overall.qualification.reasons.join("; ")})`}
          </dd>
        </dl>
        {r.overall.verdict_cap.applied && (
          <p style={{ color: "var(--state-danger)", marginBottom: 0 }}>
            {r.overall.verdict_cap.label}: {r.overall.verdict_cap.reasons.join("; ")} —
            caused by {r.overall.verdict_cap.block_ids.join(", ")}.
          </p>
        )}
      </section>

      {r.blocks.length > 0 && (
        <section className="card">
          <h2>{capitalised(noun.many)}</h2>
          <table className="report-table">
            <thead>
              <tr>
                <th>#</th>
                <th>{capitalised(noun.one)}</th>
                <th>Applicability</th>
                <th>Raw</th>
                <th>Level</th>
                <th>Coverage</th>
                <th>Evidence</th>
                <th>Qualification</th>
              </tr>
            </thead>
            <tbody>
              {r.blocks.map((b) => (
                <tr key={b.block_id}>
                  <td>{b.sequence}</td>
                  <td>
                    {b.title}
                    {b.is_critical ? " ▲" : ""}
                  </td>
                  <td>{b.applicability.replaceAll("_", " ")}</td>
                  <td>{num(b.calculated_raw_score)}</td>
                  <td>
                    {b.calculated_maturity_level === null ? (
                      "—"
                    ) : b.caps_applied.length > 0 ? (
                      <>
                        <s>{b.uncapped_maturity_level}</s> {b.calculated_maturity_level}{" "}
                        (capped)
                      </>
                    ) : (
                      b.calculated_maturity_level
                    )}
                  </td>
                  <td>
                    {b.coverage.scored}/{b.coverage.required}
                    {b.coverage.met ? "" : " (below minimum)"}
                  </td>
                  <td>
                    {b.evidence.required === 0
                      ? "none required"
                      : `${b.evidence.linked}/${b.evidence.required}${b.evidence.sufficient ? "" : " (insufficient)"}`}
                  </td>
                  <td>{b.qualification.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {r.dimensions.length > 0 && (
        <section className="card">
          <h2>Dimensions</h2>
          <p style={{ color: "var(--text-muted)", fontSize: "var(--lf-body-size)", marginTop: 0 }}>
            Diagnostic only — never part of the maturity calculation (ADR-25);
            every mean travels with its denominator.
          </p>
          <table className="report-table">
            <thead>
              <tr>
                <th>Dimension</th>
                <th>Mean</th>
                <th>Criteria</th>
                <th>{capitalised(noun.many)}</th>
              </tr>
            </thead>
            <tbody>
              {r.dimensions.map((d) => (
                <tr key={d.dimension}>
                  <td>
                    {d.dimension} — {d.title}
                  </td>
                  <td>{d.mean === null ? "not yet scored" : num(d.mean)}</td>
                  <td>
                    {d.criterion_count}/{d.defined_criterion_count}
                  </td>
                  <td>
                    {d.artifact_count}/{d.defined_artifact_count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section className="card">
        <h2>Engine findings</h2>
        {r.findings.length === 0 ? (
          <p style={{ color: "var(--text-muted)", margin: 0 }}>
            The engine raised no findings on this evaluation.
          </p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
            {[...r.findings]
              .sort((a, b) => SEV_ORDER[a.severity] - SEV_ORDER[b.severity])
              .map((f) => (
                <li key={f.id}>
                  <strong>{f.severity}</strong> — {f.text}{" "}
                  <span style={{ color: "var(--text-muted)" }}>
                    ({f.block_id}
                    {f.criterion_ids.length > 0 ? `: ${f.criterion_ids.join(", ")}` : ""})
                  </span>
                </li>
              ))}
          </ul>
        )}
      </section>

      {r.recommendations.length > 0 && (
        <section className="card">
          <h2>Corrective-action proposals</h2>
          <p style={{ color: "var(--text-muted)", fontSize: "var(--lf-body-size)", marginTop: 0 }}>
            Deterministic proposals from the instrument's own corrective-action
            text — owned actions live in the register below (ADR-18).
          </p>
          <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
            {r.recommendations.map((p) => (
              <li key={p.id}>
                <strong>{p.priority}</strong> ({p.treatment}) — {p.text}{" "}
                <span style={{ color: "var(--text-muted)" }}>
                  ({p.block_id}: {p.criterion_id}, scored {p.score})
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="card">
        <h2>Managed findings register</h2>
        {model.register.findings.length === 0 ? (
          <p style={{ color: "var(--text-muted)", margin: 0 }}>
            No managed findings recorded on this run.
          </p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
            {model.register.findings.map(({ finding, origin }) => (
              <li key={finding.id} style={{ marginBottom: "0.3rem" }}>
                <strong>{finding.severity}</strong> [{finding.status}] {finding.title}
                {finding.description && (
                  <span style={{ color: "var(--text-muted)" }}> — {finding.description}</span>
                )}
                {origin && (
                  <div style={{ fontSize: "var(--lf-meta-size)", color: "var(--text-muted)" }}>
                    Promoted from engine finding {origin.kernel_finding_key} (generation{" "}
                    {origin.promotion_generation}); original severity{" "}
                    {origin.original_severity}
                    {origin.promotion_rationale && `; rationale: ${origin.promotion_rationale}`}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card">
        <h2>Actions register</h2>
        {model.register.actions.length === 0 ? (
          <p style={{ color: "var(--text-muted)", margin: 0 }}>
            No actions address this run's findings.
          </p>
        ) : (
          <table className="report-table">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Status</th>
                <th>Action</th>
                <th>Owner</th>
                <th>Due</th>
                <th>Addresses</th>
              </tr>
            </thead>
            <tbody>
              {model.register.actions.map(({ action, ownerEmail, addresses }) => (
                <tr key={action.id}>
                  <td>{action.priority}</td>
                  <td>{action.status}</td>
                  <td>
                    {action.title}
                    {action.required_outcome && (
                      <div style={{ fontSize: "var(--lf-meta-size)", color: "var(--text-muted)" }}>
                        Done means: {action.required_outcome}
                      </div>
                    )}
                    {action.status === "completed" && action.completion_statement && (
                      <div style={{ fontSize: "var(--lf-meta-size)" }}>
                        Completed: {action.completion_statement}
                      </div>
                    )}
                  </td>
                  <td>{ownerEmail}</td>
                  <td>{action.due_date ?? "—"}</td>
                  <td>{addresses.join("; ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <footer className="report-footer">
        <div>{model.template.label}</div>
        <dl className="report-grid" style={{ fontSize: "var(--lf-meta-size)" }}>
          <dt>Template</dt>
          <dd>
            {model.template.id} v{model.template.version}
          </dd>
          <dt>Evaluation</dt>
          <dd>
            {model.evaluation.id} (seq {model.evaluation.seq}),{" "}
            {model.evaluation.policyId} under engine contract{" "}
            {model.evaluation.engineContract}, {model.evaluation.canonicalization}
          </dd>
          <dt>Definition hash</dt>
          <dd className="mono">{model.evaluation.definitionHash}</dd>
          <dt>Policy hash</dt>
          <dd className="mono">{model.evaluation.policyHash}</dd>
          <dt>Input hash</dt>
          <dd className="mono">{model.evaluation.inputHash}</dd>
          <dt>Result hash</dt>
          <dd className="mono">{model.evaluation.resultHash}</dd>
        </dl>
        <p style={{ margin: "0.4rem 0 0", color: "var(--text-muted)", fontSize: "var(--lf-meta-size)" }}>
          Rendered verbatim from the frozen evaluation snapshot and the managed
          register; the console computes nothing. Replaying the recorded inputs
          under the recorded engine reproduces the result hash above.
        </p>
        {/* G14: printed from the template, never written in this screen. */}
        <p style={{ margin: "0.4rem 0 0", color: "var(--text-muted)", fontSize: "var(--lf-meta-size)" }}>
          {model.template.scoreInterpretation}
        </p>
      </footer>
    </div>
  );
}
